import { APIEndpoints } from '@/@admin/types/constants';
import {
    TrialAuditEvent,
    TrialMonitoringParams,
    TrialMonitoringResponse,
} from '@/@admin/types/trial-monitoring';
import { buildCacheKey } from '@/@admin/utils';
import { tracking } from '@/lib/features/tracking';
import { portalApi } from '@/lib/http/axios';

import { BaseService } from './base.service';

/**
 * Service for the Trial Monitoring dashboard (CS-facing internal tool).
 */
export class TrialMonitoringService extends BaseService {
    constructor() {
        super(portalApi);
    }

    async updateTrialDates(
        orgId: number,
        data: { trialStartDate?: string | null; trialEndDate?: string | null },
    ): Promise<{ trialStartDate: string | null; trialEndDate: string | null }> {
        try {
            const { data: result } = await this.axiosInstance.patch<{
                trialStartDate: string | null;
                trialEndDate: string | null;
            }>(`${APIEndpoints.TRIAL_MONITORING}/orgs/${orgId}/trial-dates`, data);
            this.clearCache();
            return result;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to update trial dates for org ${orgId}: ${errorMessage}`),
                'studio.TrialMonitoringService.updateTrialDates',
                { orgId },
            );
            throw error;
        }
    }

    async getTrialAuditEvents(orgId: number): Promise<TrialAuditEvent[]> {
        try {
            const { data } = await this.axiosInstance.get<TrialAuditEvent[]>(
                `${APIEndpoints.TRIAL_MONITORING}/orgs/${orgId}/audit`,
            );
            return data;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get trial audit events for org ${orgId}: ${errorMessage}`),
                'studio.TrialMonitoringService.getTrialAuditEvents',
                { orgId },
            );
            throw error;
        }
    }

    async syncOrgCsm(orgId: number): Promise<{ csmName: string | null }> {
        try {
            const { data } = await this.axiosInstance.post<{ csmName: string | null }>(
                `${APIEndpoints.TRIAL_MONITORING}/orgs/${orgId}/sync-csm`,
            );
            this.clearCache();
            return data;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to sync CSM for org ${orgId}: ${errorMessage}`),
                'studio.TrialMonitoringService.syncOrgCsm',
                { orgId },
            );
            throw error;
        }
    }

    async getTrialMonitoring(params: TrialMonitoringParams): Promise<TrialMonitoringResponse> {
        try {
            const cacheKey = buildCacheKey('trialMonitoring', {
                page: params.page,
                limit: params.limit,
                search: params.search,
                sortBy: params.sortBy,
                sortDir: params.sortDir,
            });
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;

            const { data } = await this.axiosInstance.get<TrialMonitoringResponse>(
                APIEndpoints.TRIAL_MONITORING,
                { params },
            );

            this.setCachedData(cacheKey, data);
            return data;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get trial monitoring data: ${errorMessage}`),
                'studio.TrialMonitoringService.getTrialMonitoring',
                { params },
            );
            throw error;
        }
    }
}

export const trialMonitoringService = new TrialMonitoringService();
